/**
 * Error Type Definitions for Structured Logging
 *
 * Four categories:
 * - BACKEND_API: Axios/API call failures (HTTP errors from backend endpoints)
 * - SERVER_SIDE: SSR errors (React Server Components, proxy, etc.)
 * - CLIENT_SIDE: CSR errors (hydration, runtime, React error boundaries)
 * - COMPANY_OUT_OF_SUBSCRIPTION: a tenant went suspended/deactivated. Not a fault —
 *   a business-state transition we want to see per company.
 */

export enum ErrorType {
  BACKEND_API = "backend_api",
  SERVER_SIDE = "server_side",
  CLIENT_SIDE = "client_side",
  COMPANY_OUT_OF_SUBSCRIPTION = "company_out_of_subscription",
}

/** Who is at fault. `type` only says which layer reported the error. */
export enum FailureKind {
  API_HTTP = "api_http",
  API_NETWORK = "api_network",
  FRONTEND_EXCEPTION = "frontend_exception",
}

export interface ErrorReportMeta {
  failureKind?: FailureKind;
  /** Short code shown to the customer, to match a report to a log entry. */
  errorRef?: string;
  /** The user action that failed, e.g. "add_to_cart". */
  context?: string;
  extra?: Record<string, string | number | boolean | null | undefined>;
  /** Log even if the status code is in SKIPPED_API_STATUS_CODES. */
  forceLog?: boolean;
}

/**
 * Status codes whose BACKEND_API errors are intentionally NOT logged to Graylog —
 * expected, non-actionable business outcomes rather than operational faults.
 *
 * - 404: missing resource (noisy, expected).
 * - 430: shop suspended / out of subscription. Every request from a lapsed tenant
 *   returns this by design; logging each one floods Graylog and buries real failures.
 * - 431: shop deactivated. Same class of expected outcome as 430 (see MainError.tsx,
 *   which maps 430 -> SuspendShop and 431 -> DeactivateShop).
 *
 * IMPORTANT: this list is scoped to `ErrorType.BACKEND_API` by every consumer. That is
 * what lets the once-per-tenant `COMPANY_OUT_OF_SUBSCRIPTION` event still reach Graylog
 * while the per-request noise is dropped. Do not widen the guard to other error types.
 *
 * Note there is deliberately no `4301` here: it is not an HTTP status (consumers compare
 * against `error.response.status`), so it would be a dead entry that could one day
 * collide with a real status.
 */
export const SKIPPED_API_STATUS_CODES = [404, 430, 431];

export interface BackendApiErrorData extends ErrorReportMeta {
  type: ErrorType.BACKEND_API;
  /** The API endpoint that failed (e.g. /api/products/123) */
  endpoint: string;
  /** HTTP method (GET, POST, PUT, DELETE, PATCH) */
  method: string;
  /** HTTP status code from the response */
  statusCode: number;
  /** Response body (truncated if too large) */
  responseBody?: string;
  /** Request payload summary */
  requestPayload?: string;
  /** Primary error message */
  errorMessage: string;
  /** React Query key if available */
  queryKey?: string;
  /** Browser user agent */
  userAgent?: string;
  /** ISO timestamp */
  timestamp: string;
  /** The host/domain */
  host: string;
  /** The URL the user was visiting */
  url: string;
}

export interface ServerSideErrorData extends ErrorReportMeta {
  type: ErrorType.SERVER_SIDE;
  /** Primary error message */
  errorMessage: string;
  /** Error constructor name (e.g. TypeError, RangeError) */
  errorName: string;
  /** Full stack trace */
  stack: string;
  /** Next.js error digest for deduplication */
  digest?: string;
  /** Component hierarchy leading to the crash */
  componentTree?: string;
  /** The route path where the error occurred */
  routePath?: string;
  /** Rendering context: RSC, SSR, middleware, API route */
  renderingContext?: string;
  /** ISO timestamp */
  timestamp: string;
  /** The host/domain */
  host: string;
  /** The URL the user was visiting */
  url: string;
}

export interface ClientSideErrorData extends ErrorReportMeta {
  type: ErrorType.CLIENT_SIDE;
  /** Primary error message */
  errorMessage: string;
  /** Error constructor name */
  errorName: string;
  /** Full stack trace */
  stack: string;
  /** React component stack from error boundary */
  componentTree?: string;
  /** The route path where the error happened */
  routePath: string;
  /** Browser user agent */
  userAgent: string;
  /** ISO timestamp */
  timestamp: string;
  /** The host/domain */
  host: string;
  /** The URL the user was visiting */
  url: string;
}

/**
 * A tenant became unavailable for business reasons. Emitted ONCE per verdict (when the
 * suspension is first recorded in Redis), not once per request — the proxy short-circuits
 * every subsequent request without re-checking, so a per-request event would recreate
 * exactly the log volume SKIPPED_API_STATUS_CODES exists to remove.
 */
export interface CompanyOutOfSubscriptionData extends ErrorReportMeta {
  type: ErrorType.COMPANY_OUT_OF_SUBSCRIPTION;
  /** Primary grouping key for alerting. */
  companyId: string;
  /** Host header — always available, secondary grouping key. */
  domain: string;
  /** 430 suspended, 431 deactivated */
  statusCode: number;
  /** The endpoint that reported it (e.g. /company-identification) */
  endpoint: string;
  /** Backend message text, if any */
  errorMessage: string;
  /** Where the verdict was observed */
  renderingContext: "verdict-write" | "proxy-short-circuit";
  /** ISO timestamp */
  timestamp: string;
  /** The host/domain */
  host: string;
  /** The URL the user was visiting */
  url: string;
}

export type StructuredErrorData =
  | BackendApiErrorData
  | ServerSideErrorData
  | ClientSideErrorData
  | CompanyOutOfSubscriptionData;
